<template>
  <span>
    <!-- No <notifications> host here: UserSearch.vue provides the single page-level host. -->
    <a @click.prevent="open" href="#" class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">
      <i class="fas fa-plus-circle mr-3 w-5 text-pink-500 group-hover:text-pink-700"></i>
      Allow Another Campaign
    </a>

    <transition v-if="isOpen"
                enter-class="transition ease-out duration-100 transform"
                enter-active-class="opacity-0"
                enter-to-class="opacity-100"
                leave-class="transition ease-in duration-200 transform"
                leave-active-class="opacity-100"
                leave-to-class="opacity-0">
      <div class="fixed z-50 inset-0 overflow-y-auto">
        <div class="flex items-start justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div class="fixed inset-0 transition-opacity" aria-hidden="true">
            <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>
          <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

          <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all lg:my-8 lg:align-middle lg:max-w-2xl lg:w-1/2 lg:p-6">
            <div class="flex justify-between items-center pb-3">
              <h3 class="text-lg leading-6 font-medium text-gray-900">
                Allow another campaign for <span class="font-bold">{{ user.name }} ({{ user.email }})</span>
              </h3>
              <div class="modal-close cursor-pointer z-50">
                <svg @click="isOpen = false" class="fill-current text-black" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18"><path d="M14.53 4.53l-1.06-1.06L9 7.94 4.53 3.47 3.47 4.53 7.94 9l-4.47 4.47 1.06 1.06L9 10.06l4.47 4.47 1.06-1.06L10.06 9z"></path></svg>
              </div>
            </div>

            <p class="text-sm text-gray-600 mb-4">
              Normally a user may have one campaign under each parent. This grants
              <strong>one</strong> extra registration under the parent you pick. The user
              then registers on their own as usual; nothing is created here.
            </p>

            <div class="mb-4">
              <p class="px-1 text-sm font-medium text-gray-700">Parent campaign (e.g. this year's Dig Pink Rally)</p>
              <campaign-search-input :show-only-top-level-campaigns="false" />
              <p v-if="selectedCampaign" class="px-1 mt-1 text-sm text-gray-800">
                Selected: <span class="font-bold">{{ selectedCampaign.name }} ({{ selectedCampaign.id }})</span>
              </p>
            </div>

            <div class="mb-4">
              <label class="px-1 text-sm font-medium text-gray-700" for="aac-note">Note (optional)</label>
              <input id="aac-note" v-model="note" type="text" maxlength="500" placeholder="e.g. Second school event this fall, approved by Rick"
                     class="mt-1 block w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500">
            </div>

            <div class="border-t border-gray-200 pt-3">
              <p class="text-sm font-medium text-gray-700 mb-2">Existing overrides for this user</p>
              <p v-if="loading" class="text-sm text-gray-500">Loading…</p>
              <p v-else-if="overrides.length === 0" class="text-sm text-gray-500">None yet.</p>
              <ul v-else class="divide-y divide-gray-200 text-sm">
                <li v-for="o in overrides" :key="o.id" class="py-2 flex items-start justify-between gap-3">
                  <div>
                    <div class="font-medium text-gray-900">{{ o.parent_campaign_name || ('Campaign #' + o.parent_campaign_id) }}</div>
                    <div class="text-gray-500">
                      <span :class="statusClass(o.status)" class="inline-block px-2 py-0.5 text-xs font-semibold uppercase mr-1">{{ o.status }}</span>
                      granted by {{ o.granted_by_name || 'unknown' }} on {{ o.created_at }}
                      <span v-if="o.status === 'used'"> · used by {{ o.consumed_campaign_name || ('campaign #' + o.consumed_campaign_id) }}</span>
                    </div>
                    <div v-if="o.note" class="text-gray-600 italic">{{ o.note }}</div>
                  </div>
                  <button v-if="o.status === 'available'" @click="revoke(o)" type="button"
                          class="shrink-0 text-xs text-red-600 hover:text-red-800 underline">Revoke</button>
                </li>
              </ul>
            </div>

            <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button @click="grant" :disabled="saving" type="button"
                      class="w-full inline-flex justify-center border border-transparent shadow-sm px-4 py-2 bg-pink-500 text-base font-medium text-white hover:bg-pink-700 focus:outline-none focus:ring-offset-2 focus:ring-pink-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50">
                Grant Override
              </button>
              <button @click="isOpen = false" type="button"
                      class="mt-3 w-full inline-flex justify-center border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </span>
</template>

<script>
import { buildHttpErrorNotification } from '../../http-error-notifier'

export default {
  name: 'AllowAnotherCampaign',
  props: {
    user: null,
  },
  data() {
    return {
      isOpen: false,
      loading: false,
      saving: false,
      note: '',
      overrides: [],
      selectedCampaign: null,
    }
  },
  created() {
    // campaign-search-input broadcasts on the global emitter; only act while this modal is open.
    this.emitter.on('search-selected-campaign', (campaign) => {
      if (this.isOpen) this.selectedCampaign = campaign || null
    })
  },
  methods: {
    open() {
      this.isOpen = true
      this.selectedCampaign = null
      this.note = ''
      this.emitter.emit('search-selected-campaign')
      this.load()
    },
    load() {
      this.loading = true
      axios.get(`/admin/users/${this.user.id}/registration-overrides`)
        .then(({ data }) => { this.overrides = data.overrides || [] })
        .catch(error => this.$notify(buildHttpErrorNotification(error)))
        .finally(() => { this.loading = false })
    },
    grant() {
      if (!this.selectedCampaign) {
        this.$notify({ text: 'Pick the parent campaign the user should be allowed to register under again.', type: 'error' })
        return
      }
      this.saving = true
      axios.post('/admin/registration-overrides', {
        user_id: this.user.id,
        parent_campaign_id: this.selectedCampaign.id,
        note: this.note || null,
      })
        .then(({ data }) => {
          this.overrides.unshift(data.override)
          this.note = ''
          this.$notify({ text: `${this.user.name} may now register one more campaign under ${data.override.parent_campaign_name}.`, type: 'success' })
        })
        .catch(error => {
          const msg = error?.response?.data?.message
          this.$notify(error?.response?.status === 422 && msg ? { text: msg, type: 'error' } : buildHttpErrorNotification(error))
        })
        .finally(() => { this.saving = false })
    },
    revoke(o) {
      if (!window.confirm(`Revoke the unused override for ${o.parent_campaign_name || 'this parent'}?`)) return
      axios.delete(`/admin/registration-overrides/${o.id}`)
        .then(({ data }) => {
          const i = this.overrides.findIndex(x => x.id === o.id)
          if (i !== -1) this.overrides.splice(i, 1, data.override)
          this.$notify({ text: 'Override revoked.', type: 'success' })
        })
        .catch(error => this.$notify(buildHttpErrorNotification(error)))
    },
    statusClass(status) {
      return {
        available: 'bg-green-100 text-green-800',
        used: 'bg-gray-100 text-gray-700',
        revoked: 'bg-red-100 text-red-700',
      }[status] || 'bg-gray-100 text-gray-700'
    },
  },
}
</script>
